import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { StreamPoint } from '../utils/workoutStream';

/** Carto light basemap — matches WorkoutAnalysisView. */
export const MAP_TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
export const MAP_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>, &copy; <a href="https://carto.com/">CARTO</a>';

let leafletIconsFixed = false;

/** Fix Leaflet default icon paths (broken by bundlers). */
export function ensureLeafletIcons(): void {
  if (leafletIconsFixed) return;
  leafletIconsFixed = true;
  // @ts-expect-error - Leaflet internals
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

/** FIT semicircles or already-decoded degrees → WGS84 degrees. */
export function fitCoordToDegrees(value: number, axis: 'lat' | 'lon'): number {
  const limit = axis === 'lat' ? 90 : 180;
  if (Math.abs(value) <= limit) return value;
  return value * (180 / 2 ** 31);
}

/** Centers the map on route bounds (no extra setZoom — avoids Leaflet _leaflet_pos races). */
export function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length < 2 || !map.getContainer()?.isConnected) return;
    map.fitBounds(L.latLngBounds(positions), { padding: [28, 28], maxZoom: 15, animate: false });
  }, [map, positions]);
  return null;
}

/** Initial fit — once per fitKey (WorkoutAnalysisView, workout detail map). */
export function FitBoundsOnce({ positions, fitKey }: { positions: [number, number][]; fitKey: string }) {
  const map = useMap();
  const doneRef = useRef<string | null>(null);
  useEffect(() => {
    if (positions.length < 2 || doneRef.current === fitKey || !map.getContainer()?.isConnected) return;
    doneRef.current = fitKey;
    map.fitBounds(L.latLngBounds(positions), { padding: [24, 24], maxZoom: 15, animate: false });
  }, [map, positions, fitKey]);
  return null;
}

/** Wheel zoom on map — scroll zooms the map; wheel does not bubble to parent panels. */
export function MapScrollWheelGuard() {
  const map = useMap();
  useEffect(() => {
    map.scrollWheelZoom.enable();
    const el = map.getContainer();
    const onWheel = (e: WheelEvent) => {
      e.stopPropagation();
    };
    el.addEventListener('wheel', onWheel, { capture: true });
    return () => {
      map.scrollWheelZoom.disable();
      el.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [map]);
  return null;
}

/** Debounced invalidateSize — avoids tile-request storms during panel scroll/resize. */
export function DebouncedInvalidateMapSize({ height }: { height?: number } = {}) {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (!map.getContainer()?.isConnected) return;
      map.invalidateSize({ pan: false, animate: false });
    }, 120);
    return () => clearTimeout(id);
  }, [map, height]);
  return null;
}

/** SVG circle marker for route start/end — color matches workout slot. */
export function routeEndpointIcon(color: string, kind: 'start' | 'end') {
  return L.divIcon({
    className: '',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `<svg width="20" height="20" viewBox="0 0 24 24" style="display:block">
      <circle cx="12" cy="12" r="9" fill="${color}" stroke="white" stroke-width="2.5"/>
      ${kind === 'start' ? '<circle cx="12" cy="12" r="3" fill="white" opacity="0.85"/>' : ''}
    </svg>`,
  });
}

/** Slice a GPS route to a stream time window (matches WorkoutAnalysisView selection logic). */
export function sliceGpsRouteByTimeRange(
  stream: StreamPoint[],
  route: [number, number][],
  startSec: number,
  endSec: number,
): [number, number][] {
  if (!route.length || !stream.length) return [];
  let gpsStart = -1;
  let gpsEnd = -1;
  let gpsIdx = -1;

  for (let i = 0; i < stream.length; i++) {
    const p = stream[i];
    if (p.positionLat == null || p.positionLong == null) continue;
    gpsIdx++;
    if (p.timeSeconds >= startSec && gpsStart === -1) gpsStart = gpsIdx;
    if (p.timeSeconds <= endSec) gpsEnd = gpsIdx;
  }

  if (gpsStart === -1 || gpsEnd === -1 || gpsStart > gpsEnd) return [];
  const sel = route.slice(gpsStart, gpsEnd + 1);
  return sel.length >= 2 ? sel : [];
}

export function colorWithAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(h)) return `color-mix(in srgb, ${hex} ${Math.max(0, Math.min(1, alpha)) * 100}%, transparent)`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function MapLegend({ items }: { items: { color: string; label: string }[] }) {
  if (!items.length) return null;
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 10,
        left: 10,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '6px 10px',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.92)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        fontSize: 11,
        fontWeight: 700,
        color: '#1A1F2B',
      }}
    >
      {items.map(item => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 14,
              height: 4,
              borderRadius: 2,
              background: item.color,
              flexShrink: 0,
            }}
          />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}
